// src/screens/dashboard/PawnCreatePage.jsx
import React, { useEffect, useMemo, useState, forwardRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller, useWatch } from "react-hook-form";
import { CalendarDays, ChevronDown, X, FileText } from "lucide-react";
import { ReactComponent as Smartphone } from "../../assets/images/Smartphone.svg";
import { ReactComponent as Phone } from "../../assets/images/Phone.svg";
import { ReactComponent as Banknote } from "../../assets/images/Banknote.svg";
import { useMasterOptions } from "../../hooks/useMasterOptions";


import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import th from "date-fns/locale/th";

import PageHeader from "../../components/layout/PageHeader";
import { createHttpClient } from "../../api/client";
import config from "../../config/env";

registerLocale("th", th);

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function parseBuyInToISO(text) {
  if (!text) return null;

  const m = String(text).match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/
  );
  if (!m) return null;

  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  const HH = Number(m[4]);
  const MM = Number(m[5]);
  const SS = Number(m[6] || 0);

  const d = new Date(yyyy, mm - 1, dd, HH, MM, SS);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function toDateValue(v) {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;

  const thai = parseThaiDateString(v);
  if (thai) return thai;

  const iso = parseBuyInToISO(v);
  if (iso) return new Date(iso);

  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function getAutoDueDateFromBuyIn(v) {
  const buyIn = toDateValue(v);
  if (!buyIn) return null;
  const due = new Date(buyIn);
  due.setDate(due.getDate() + 30);
  return due;
}

function formatThaiDateTimeFromISO(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";

  const y = d.getFullYear() + 543;
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${m}/${y} ${hh}.${mi} น.`;
}

function relationToIdString(v) {
  if (v == null || v === "") return "";
  if (typeof v === "object") {
    if (v.id != null) return String(v.id);
    return "";
  }
  return String(v);
}

function formatThaiDateInput(raw) {
  const sanitized = String(raw || "").replace(/[^\d/]/g, "");
  if (!sanitized) return "";

  const slashCount = (sanitized.match(/\//g) || []).length;
  const parts = sanitized.split("/");
  const day = String(parts[0] || "").replace(/\D/g, "").slice(0, 2);
  const month = String(parts[1] || "").replace(/\D/g, "").slice(0, 2);
  const year = String(parts[2] || "").replace(/\D/g, "").slice(0, 4);

  let out = day;
  if (day.length < 2) return out;
  out += "/";
  if (month) out += month;
  if (month && (slashCount >= 2 || month.length >= 2 || year)) out += "/";
  if (year) out += year;
  return out;
}

function formatThaiDateBuddhist(d) {
  const dt = d instanceof Date ? d : toDateValue(d);
  if (!dt) return "";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear() + 543;
  return `${dd}/${mm}/${yyyy}`;
}

function parseThaiDateString(v) {
  const m = String(v || "").trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;

  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const buddhistYear = Number(m[3]);
  if (!Number.isFinite(dd) || !Number.isFinite(mm) || !Number.isFinite(buddhistYear)) return null;
  if (dd < 1 || dd > 31 || mm < 1 || mm > 12) return null;

  const yyyy = buddhistYear > 2400 ? buddhistYear - 543 : buddhistYear;
  const d = new Date(yyyy, mm - 1, dd);
  if (Number.isNaN(d.getTime())) return null;
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
  return d;
}

function getThaiBuddhistInputValue(v) {
  if (v == null || v === "") return "";
  if (v instanceof Date) return formatThaiDateBuddhist(v);
  return String(v);
}

function ensureSelectedOption(options, selectedValue, selectedLabel) {
  if (!selectedValue) return options || [];
  const exists = (options || []).some((o) => String(o?.value) === String(selectedValue));
  if (exists) return options || [];
  return [{ value: String(selectedValue), label: String(selectedLabel || "-") }, ...(options || [])];
}

const HARD_WARRANTY_OPTIONS = [
  { value: "ประกันศูนย์ 1 ปี", label: "ประกันศูนย์ 1 ปี" },
  { value: "ประกันศูนย์", label: "ประกันศูนย์" },
  { value: "ประกันหมด", label: "ประกันหมด" },
];

function normalizeWarrantyType(value) {
  const v = String(value || "").trim();
  if (!v) return "";
  if (v === "ประกันเหลือ" || v === "ประกันศูนย์") return "ประกันศูนย์";
  if (v === "ประกันหมด") return "ประกันหมด";
  if (v.includes("ประกันศูนย์")) return "ประกันศูนย์ 1 ปี";
  return "";
}

function UnderlineInput({
  label,
  name,
  register,
  required,
  errors,
  placeholder,
  type = "text",
  defaultValue,
  maxLength,
  maxNumberLength,
  maxValue,
  onlyDigits = false,
  forceUppercase = false,
  forceAlphanumeric = false,
}) {
  const hasError = !!errors?.[name];

  const isNumberLimited =
    typeof maxNumberLength === "number" && maxNumberLength > 0;
  const hasMaxValue = Number.isFinite(maxValue);
  const shouldSanitizeNumber = isNumberLimited || hasMaxValue || onlyDigits;

  const finalType = isNumberLimited ? "number" : type;

  return (
    <div className="py-3">
      <div className="text-sm text-[#1D1616B2] font-semibold">{label}</div>

      <input
        type={finalType}
        placeholder={placeholder}
        defaultValue={defaultValue}
        maxLength={maxLength}
        inputMode={shouldSanitizeNumber ? "numeric" : undefined}
        autoCapitalize={forceUppercase || forceAlphanumeric ? "characters" : "off"}
        className={classNames(
          "mt-1 w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-[#1D161680]",
          "border-b",
          "hover:border-[#047DED]",
          (forceUppercase || forceAlphanumeric) && "uppercase",
          hasError ? "border-red-500" : "border-[#00000026]"
        )}
        {...register(name, {
          required: required ? "required" : false,
          validate: (v) => {
            if (!shouldSanitizeNumber) return true;
            const digits = String(v ?? "").replace(/\D/g, "");
            if (!digits) return true;
            if (hasMaxValue && Number(digits) > Number(maxValue)) {
              return `ต้องไม่เกิน ${maxValue}`;
            }
            if (!isNumberLimited) return true;
            return (
              digits.length <= maxNumberLength ||
              `ต้องไม่เกิน ${maxNumberLength} ตัวเลข`
            );
          },
          setValueAs: (v) => {
            if (!shouldSanitizeNumber) {
              let next = String(v ?? "");
              if (forceUppercase || forceAlphanumeric) next = next.toUpperCase();
              if (forceAlphanumeric) next = next.replace(/[^A-Z0-9]/g, "");
              return next;
            }
            const digits = String(v ?? "")
              .replace(/\D/g, "");
            if (!digits) return "";
            const cappedByValue =
              hasMaxValue && Number.isFinite(Number(digits))
                ? String(Math.min(Number(digits), Number(maxValue)))
                : digits;
            if (!isNumberLimited) return cappedByValue;
            return cappedByValue.slice(0, maxNumberLength);
          },
        })}
        onInput={(e) => {
          if (forceUppercase || forceAlphanumeric) {
            e.currentTarget.value = String(e.currentTarget.value || "").toUpperCase();
          }
          if (forceAlphanumeric) {
            e.currentTarget.value = String(e.currentTarget.value || "").replace(/[^A-Z0-9]/g, "");
          }
          if (!shouldSanitizeNumber) return;
          const el = e.currentTarget;
          const digits = String(el.value ?? "")
            .replace(/\D/g, "");
          if (!digits) {
            el.value = "";
            return;
          }
          let next = digits;
          if (hasMaxValue && Number.isFinite(Number(next))) {
            next = String(Math.min(Number(next), Number(maxValue)));
          }
          if (isNumberLimited) {
            next = next.slice(0, maxNumberLength);
          }
          el.value = next;
        }}
        onWheel={(e) => {
          if (isNumberLimited) e.currentTarget.blur();
        }}
      />

      {hasError && (
        <div className="mt-1 text-xs text-red-600">
          {errors?.[name]?.message || "required"}
        </div>
      )}
    </div>
  );
}

const UnderlineDateInput = forwardRef(function UnderlineDateInput(
  { value, onClick, onChange, onBlur, placeholder, hasError, showTime, disabled },
  ref
) {
  const isEmpty = !value;

  return (
    <input
      type="text"
      ref={ref}
      disabled={disabled}
      onClick={onClick}
      onChange={onChange}
      onBlur={onBlur}
      value={value || ""}
      placeholder={placeholder || (showTime ? "เลือกวัน/เวลา" : "เลือกวันที่")}
      className={classNames(
        "mt-1 w-full bg-transparent text-sm outline-none",
        "border-b",
        "hover:border-[#047DED]",
        "min-h-[36px]",
        "text-left",
        hasError ? "border-red-500" : "border-[#00000026]",
        isEmpty ? "text-[#1D161680]" : "text-slate-900",
        disabled && "opacity-60 cursor-not-allowed"
      )}
    />
  );
});

function UnderlineDatePicker({
  label,
  name,
  control,
  errors,
  required = false,
  placeholder = "",
  defaultValue,
  showTimeSelect = false,
  timeIntervals = 5,
  dateFormat = "dd/MM/yyyy",
  disabled = false,
  thaiBuddhistInput = false,
}) {
  const hasError = !!errors?.[name];
  const parsedDefault = useMemo(() => toDateValue(defaultValue), [defaultValue]);

  return (
    <div className="py-3">
      <div className="text-sm text-[#1D1616B2] font-semibold">{label}</div>

      <Controller
        control={control}
        name={name}
        defaultValue={parsedDefault ?? null}
        rules={{ required: required ? "required" : false }}
        render={({ field }) => {
          if (thaiBuddhistInput) {
            return (
              <input
                type="text"
                disabled={disabled}
                value={getThaiBuddhistInputValue(field.value)}
                placeholder={placeholder}
                onChange={(e) => {
                  const next = formatThaiDateInput(e?.target?.value || "");
                  field.onChange(next);
                }}
                onBlur={field.onBlur}
                className={classNames(
                  "mt-1 w-full bg-transparent text-sm outline-none",
                  "border-b hover:border-[#047DED]",
                  hasError ? "border-red-500" : "border-[#00000026]",
                  disabled && "opacity-60 cursor-not-allowed"
                )}
              />
            );
          }

          return (
            <DatePicker
              selected={field.value ? toDateValue(field.value) : null}
              onChange={(d) => field.onChange(d)}
              onBlur={field.onBlur}
              disabled={disabled}
              placeholderText={placeholder}
              locale="th"
              showTimeSelect={showTimeSelect}
              timeIntervals={timeIntervals}
              dateFormat={showTimeSelect ? "dd/MM/yyyy HH:mm" : dateFormat}
              wrapperClassName="w-full"
              popperClassName="z-[9999]"
              customInput={
                <UnderlineDateInput
                  hasError={hasError}
                  placeholder={placeholder}
                  showTime={showTimeSelect}
                  disabled={disabled}
                />
              }
              popperPlacement="bottom-start"
            />
          );
        }}
      />

      {hasError && (
        <div className="mt-1 text-xs text-red-600">
          {errors?.[name]?.message || "required"}
        </div>
      )}
    </div>
  );
}

function UnderlineSelect({
  label,
  name,
  register,
  required,
  rules,
  errors,
  placeholder = "เลือกข้อมูล",
  options = [],
  disabled = false,
}) {
  const hasError = !!errors?.[name];

  return (
    <div className="py-3">
      <div className="text-sm text-[#1D1616B2] font-semibold">{label}</div>

      <div className="relative mt-1">
        <select
          disabled={disabled}
          className={classNames(
            "w-full appearance-none bg-transparent text-sm outline-none",
            "border-b pr-10 text-black",
            "hover:border-[#047DED]",
            disabled && "opacity-60 cursor-not-allowed",
            hasError ? "border-red-500" : "border-[#00000026]"
          )}
          defaultValue=""
          {...register(name, {
            required: required ? "required" : false,
            ...(rules || {}),
          })}
        >
          <option value="" disabled className="text-slate-500">
            {placeholder}
          </option>
          {options.map((o) => (
            <option key={o.value} value={o.value} className="text-black">
              {o.label}
            </option>
          ))}
        </select>

        <span className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2">
          <ChevronDown className="h-4 w-4 opacity-60" />
        </span>
      </div>

      {hasError && (
        <div className="mt-1 text-xs text-red-600">
          {errors?.[name]?.message || "required"}
        </div>
      )}
    </div>
  );
}

function UnderlineTextarea({
  label,
  name,
  register,
  required,
  errors,
  placeholder,
  rows = 3,
}) {
  const hasError = !!errors?.[name];
  return (
    <div className="py-3">
      <div className="text-sm text-[#1D1616B2] font-semibold">{label}</div>
      <textarea
        rows={rows}
        placeholder={placeholder}
        className={classNames(
          "mt-1 w-full resize-none bg-transparent text-sm outline-none placeholder:text-[#1D161680]",
          "border-b",
          "hover:border-[#047DED]",
          hasError ? "border-red-500" : "border-[#00000026]"
        )}
        {...register(name, { required: required ? "required" : false })}
      />
      {hasError && (
        <div className="mt-1 text-xs text-red-600">
          {errors?.[name]?.message || "required"}
        </div>
      )}
    </div>
  );
}

function RedeemModal({ open, onClose, onSubmit, defaultPhone = "" }) {
  const [form, setForm] = useState({
    phone: defaultPhone,
    interest: "",
    cash: "",
    transfer: "",
    card: "",
  });

  useEffect(() => {
    if (!open) return;
    setForm((s) => ({ ...s, phone: defaultPhone || s.phone }));
  }, [open, defaultPhone]);

  const remaining = useMemo(() => {
    const n = (x) => Number(String(x ?? "").replace(/,/g, "")) || 0;
    const mustPay = n(form.interest);
    const paid = n(form.cash) + n(form.transfer) + n(form.card);
    return Math.max(mustPay - paid, 0);
  }, [form]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[300] bg-black/50" onClick={onClose} />
      <div className="fixed inset-0 z-[301] flex items-center justify-center p-4">
        <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-5 border-b border-black/10">
            <div className="text-lg font-extrabold text-slate-900">ไถ่ถอนการจำนำ</div>
            <button
              type="button"
              onClick={onClose}
              className="h-9 w-9 rounded-full hover:bg-black/5 flex items-center justify-center"
              aria-label="close"
            >
              <X className="h-5 w-5 text-slate-700" />
            </button>
          </div>

          <div className="p-6">
            <div className="space-y-3">
              <div>
                <div className="text-sm text-[#1D1616B2] font-semibold">เบอร์โทรของผู้ฝากขาย</div>
                <input
                  value={form.phone}
                  maxLength={10}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 10 && /^\d*$/.test(value)) {
                      setForm((s) => ({ ...s, phone: value }));
                    }
                  }}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-300"
                  placeholder="กรอกข้อมูล"
                />
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <div className="text-sm text-[#1D1616B2] font-semibold">ดอกเบี้ย</div>
                  <input
                    value={form.interest}
                    onChange={(e) => setForm((s) => ({ ...s, interest: e.target.value }))}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-300"
                    placeholder="฿0"
                  />
                </div>

                <div>
                  <div className="text-sm text-[#1D1616B2] font-semibold">เงินสด</div>
                  <input
                    value={form.cash}
                    onChange={(e) => setForm((s) => ({ ...s, cash: e.target.value }))}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-300"
                    placeholder="฿0"
                  />
                </div>

                <div>
                  <div className="text-sm text-[#1D1616B2] font-semibold">เงินโอน</div>
                  <input
                    value={form.transfer}
                    onChange={(e) => setForm((s) => ({ ...s, transfer: e.target.value }))}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-300"
                    placeholder="฿0"
                  />
                </div>

                <div>
                  <div className="text-sm text-[#1D1616B2] font-semibold">บัตรเครดิต</div>
                  <input
                    value={form.card}
                    onChange={(e) => setForm((s) => ({ ...s, card: e.target.value }))}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-300"
                    placeholder="฿0"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <div className="text-sm font-semibold text-slate-700">ยอดที่ต้องชำระ:</div>
                <div className="text-sm font-extrabold text-[#047DED]">฿{remaining.toLocaleString("th-TH")}</div>
              </div>
            </div>
          </div>

          <div className="px-6 pb-6 flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="h-11 flex-1 rounded-xl border border-slate-200 bg-white text-sm font-bold hover:bg-slate-50"
            >
              ยกเลิก
            </button>
            <button
              type="button"
              onClick={() => onSubmit?.(form)}
              className="h-11 flex-1 rounded-xl bg-[#111] text-white text-sm font-bold hover:opacity-95 active:scale-[0.99]"
            >
              ไถ่ถอน
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function normalizeUploadUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = String(config?.apiImg || "").replace(/\/+$/, "");
  if (!base) return url;
  if (url.startsWith("/")) return `${base}${url}`;
  return `${base}/${url}`;
}

function extractPawnReportPdfUrlFromResponse(rawResponse) {
  const payload = rawResponse?.data ?? rawResponse;
  const entity = payload?.data?.attributes
    ? { id: payload.data.id, ...payload.data.attributes }
    : payload?.data
      ? payload.data
      : payload;

  return normalizeUploadUrl(
    entity?.report_pdf_path ||
    payload?.report_pdf_path ||
    payload?.data?.report_pdf_path ||
    payload?.data?.attributes?.report_pdf_path ||
    ""
  );
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchPawnReportPdfUrlById(http, idOrDocumentId) {
  if (!idOrDocumentId) return "";
  try {
    const res = await http.get(`/pawns/${idOrDocumentId}`);
    return extractPawnReportPdfUrlFromResponse(res?.data);
  } catch (e) {
    const ref = String(idOrDocumentId || "").trim();
    const isNumeric = /^\d+$/.test(ref);

    if (!ref || isNumeric) {
      console.error(`load pawn pdf path error id=${idOrDocumentId}:`, e);
      return "";
    }

    try {
      const res = await http.get(`/pawns`, {
        params: {
          "filters[documentId][$eq]": ref,
          "pagination[page]": 1,
          "pagination[pageSize]": 1,
        },
      });
      const first = Array.isArray(res?.data?.data) ? res.data.data[0] : null;
      return extractPawnReportPdfUrlFromResponse({ data: first || null });
    } catch (innerErr) {
      console.error(`load pawn pdf path error documentId=${idOrDocumentId}:`, innerErr);
      return "";
    }
  }
}

async function fetchPawnReportPdfUrlWithRetry(http, idOrDocumentId, retries = 12, intervalMs = 500) {
  if (!idOrDocumentId) return "";

  for (let i = 0; i < retries; i += 1) {
    const url = await fetchPawnReportPdfUrlById(http, idOrDocumentId);
    if (url) return url;
    if (i < retries - 1) {
      await delay(intervalMs);
    }
  }

  return "";
}

function ReportPreviewModal({ open, url, onClose }) {
  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[520] bg-black/60" onClick={onClose} />
      <div className="fixed inset-0 z-[521] flex items-center justify-center p-4">
        <div className="w-full max-w-5xl h-[88vh] rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-black/10 flex items-center justify-between">
            <div className="text-base font-bold text-slate-900">สัญญาจำนำ</div>
            <button
              type="button"
              onClick={onClose}
              className="h-9 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold hover:bg-slate-50"
            >
              ปิด
            </button>
          </div>
          <div className="flex-1 bg-slate-100 p-4">
            {url ? (
              <div className="h-full w-full rounded-xl overflow-hidden border border-slate-200 bg-white">
                <iframe title="pawn-report-pdf" src={url} className="h-[calc(100%-56px)] w-full border-0" />
                <div className="h-14 px-4 border-t border-slate-200 flex items-center justify-between gap-3">
                  <div className="text-xs text-slate-500 break-all">{url}</div>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 inline-flex h-9 items-center rounded-lg bg-[#047DED] px-3 text-xs font-bold text-white hover:opacity-95"
                  >
                    เปิดแท็บใหม่
                  </a>
                </div>
              </div>
            ) : (
              <div className="h-full w-full flex items-center justify-center text-sm text-slate-500">
                ไม่พบไฟล์สัญญาจำนำ
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default function PawnCreatePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const http = useMemo(() => createHttpClient(), []);

  const [header, setHeader] = useState({
    title: isEdit ? `เลขฝาก ${id}` : "เพิ่มรายการจำนำ",
    createdAtText: "-",
  });

  const [openRedeem, setOpenRedeem] = useState(false);
  const [redeemDraft, setRedeemDraft] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [apiErr, setApiErr] = useState("");
  const [reportPreviewOpen, setReportPreviewOpen] = useState(false);
  const [reportPreviewUrl, setReportPreviewUrl] = useState("");

  const [warrantyOptions, setWarrantyOptions] = useState(HARD_WARRANTY_OPTIONS);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [contactInfoOptions, setContactInfoOptions] = useState([]);

  const [filteredModelOptions, setFilteredModelOptions] = useState([]);
  const [filteredColorOptions, setFilteredColorOptions] = useState([]);
  const [filteredCapacityOptions, setFilteredCapacityOptions] = useState([]);

  const [loadingEquipments, setLoadingEquipments] = useState(false);
  const [loadingContactInfos, setLoadingContactInfos] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingColors, setLoadingColors] = useState(false);
  const [loadingCapacities, setLoadingCapacities] = useState(false);

  function ConfirmDeleteModal({
    open,
    title = "ลบรายการ",
    desc = "ต้องการลบรายการนี้ใช่หรือไม่",
    onCancel,
    onConfirm,
    loading,
  }) {
    if (!open) return null;

    return (
      <>
        <div className="fixed inset-0 z-[400] bg-black/50" onClick={onCancel} />
        <div className="fixed inset-0 z-[401] flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-black/10">
              <div className="text-lg font-extrabold text-slate-900">{title}</div>
              <button
                type="button"
                onClick={onCancel}
                className="h-9 w-9 rounded-full hover:bg-black/5 flex items-center justify-center"
                aria-label="close"
              >
                <X className="h-5 w-5 text-slate-700" />
              </button>
            </div>

            <div className="px-6 py-5 text-sm text-slate-600">{desc}</div>

            <div className="px-6 pb-6 flex items-center gap-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="h-11 flex-1 rounded-xl border border-slate-200 bg-white text-sm font-bold hover:bg-slate-50 disabled:opacity-60"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={loading}
                className="h-11 flex-1 rounded-xl bg-[#FF1607] text-white text-sm font-bold hover:opacity-95 active:scale-[0.99] disabled:opacity-60"
              >
                {loading ? "กำลังลบ..." : "ยืนยันลบ"}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  useEffect(() => {
    if (!redeemDraft) return;
    (async () => {
      setRedeeming(true);
      setApiErr("");
      try {
        const payload = {
          data: {
            pawn_status: "redeemed",
            amount: Number(String(redeemDraft.amount || 0).replace(/,/g, "")) || 0,
            interest_rate: 0,

            customer_name: "string",
            customer_phone: "string",
            social_media_1: "string",
            social_media_2: "string",

            due_date: "string",
            buy_in_date: "string",

            pawn_status: "redeemed",

            notes: "string",

            images: [],
            user_fullname: "string",
          },
        };
        const redeemRes = await http.put(`/pawns/${id}`, payload);
        const redeemReportUrlFromResponse = extractPawnReportPdfUrlFromResponse(redeemRes?.data);
        const redeemIdOrDocumentId =
          redeemRes?.data?.data?.id ||
          redeemRes?.data?.data?.documentId ||
          redeemRes?.data?.id ||
          redeemRes?.data?.documentId ||
          id;
        const redeemRefreshedReportUrl = await fetchPawnReportPdfUrlWithRetry(http, redeemIdOrDocumentId);
        const redeemReportUrl = redeemRefreshedReportUrl || redeemReportUrlFromResponse;

        setOpenRedeem(false);
        setRedeemDraft(null);
        if (redeemReportUrl) {
          setReportPreviewUrl(redeemReportUrl);
          setReportPreviewOpen(true);
        } else {
          navigate(-1);
        }
      } catch (e) {
        console.error("PUT /pawns/:id redeem error:", e);
        setApiErr("ไถ่ถอนการจำนำไม่สำเร็จ");
      } finally {
        setRedeeming(false);
      }
    })();
  }, [redeemDraft, id, http, navigate, setApiErr, setOpenRedeem, setRedeemDraft]);

  const [openDelete, setOpenDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!id) return;

    setDeleting(true);
    setApiErr("");

    try {
      await http.delete(`/pawns/${id}`);
      setOpenDelete(false);
      navigate(-1);
    } catch (e) {
      console.error("DELETE /pawns/:id error:", e);
      setApiErr("ลบรายการจำนำไม่สำเร็จ");
    } finally {
      setDeleting(false);
    }
  };

  const onInvalidSubmit = React.useCallback((formErrors) => {
    const firstName = Object.keys(formErrors || {})[0];
    if (!firstName) return;

    const el = document.querySelector(`[name="${firstName}"]`);
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (typeof el.focus === "function") {
      el.focus({ preventScroll: true });
    }
  }, []);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      buyInDateTime: null,
      dueDate: null,
      warranty_end_date: null,
      battery: "",
      cycle: "",
      warranty: "",
      imei: "",
      customerName: "",
      customerPhone: "",
      social1: "",
      social2: "",
      description: "",
      note: "",
      receivePrice: "",
      product_name: "",
      model: "",
      color: "",
      capacity: "",
      accessories: "",
    },
    mode: "onSubmit",
  });

  const selectedWarranty = useWatch({
    control,
    name: "warranty",
  });

  const selectedBuyInDateTime = useWatch({
    control,
    name: "buyInDateTime",
  });

  const selectedProductId = useWatch({
    control,
    name: "product_name",
  });

  const selectedModelId = useWatch({
    control,
    name: "model",
  });

  const isWarrantyRemaining = String(selectedWarranty || "").trim() === "ประกันศูนย์";

  useEffect(() => {
    if (isWarrantyRemaining) return;
    setValue("warranty_end_date", null);
  }, [isWarrantyRemaining, setValue]);

  const { productOptions } = useMasterOptions();

  useEffect(() => {
    let ignore = false;

    const fetchMasters = async () => {
      try {
        setLoadingEquipments(true);
        setLoadingContactInfos(true);

        const [eRes, cRes] = await Promise.all([
          http.get("/equipments"),
          http.get("/contact-infos"),
        ]);

        if (ignore) return;

        setWarrantyOptions(HARD_WARRANTY_OPTIONS);

        setEquipmentOptions(
          (eRes?.data?.data || []).map((it) => ({
            value: String(it.id),
            label: it.name || "-",
          }))
        );

        setContactInfoOptions(
          (cRes?.data?.data || []).map((it) => ({
            value: String(it.id),
            label: it.name || "-",
          }))
        );
      } catch (e) {
        console.error("load equipments/contact-infos error:", e);
      } finally {
        if (!ignore) {
          setLoadingEquipments(false);
          setLoadingContactInfos(false);
        }
      }
    };

    fetchMasters();

    return () => {
      ignore = true;
    };
  }, [http]);

  const prevSelectedProductIdRef = React.useRef(undefined);
  const prevSelectedModelIdRef = React.useRef(undefined);
  const isHydratingFromApiRef = React.useRef(isEdit);

  useEffect(() => {
    let ignore = false;

    const fetchModelsByCategory = async () => {
      const prevProductId = prevSelectedProductIdRef.current;
      prevSelectedProductIdRef.current = selectedProductId;

      if (!selectedProductId) {
        setFilteredModelOptions([]);
        setFilteredColorOptions([]);
        setFilteredCapacityOptions([]);
        if (!isHydratingFromApiRef.current) {
          setValue("model", "");
          setValue("color", "");
          setValue("capacity", "");
        }
        prevSelectedModelIdRef.current = undefined;
        return;
      }

      const shouldResetChildren =
        !isHydratingFromApiRef.current &&
        prevProductId !== undefined &&
        String(prevProductId) !== String(selectedProductId);

      try {
        setLoadingModels(true);

        if (shouldResetChildren) {
          // setValue("model", "");
          // setValue("color", "");
          // setValue("capacity", "");
          prevSelectedModelIdRef.current = undefined;
          setFilteredColorOptions([]);
          setFilteredCapacityOptions([]);
        }

        const mRes = await http.get("/models", {
          params: {
            filters: { category: { id: { $eq: selectedProductId } } },
            sort: ["sequence:asc", "id:asc"],
            pagination: { pageSize: 200 },
          },
        });

        if (ignore) return;

        setFilteredModelOptions(
          (mRes?.data?.data || []).map((it) => ({
            value: String(it.id),
            label: it.name || "-",
          }))
        );
      } catch (e) {
        console.error("load models by category error:", e);
        if (!ignore) setFilteredModelOptions([]);
      } finally {
        if (!ignore) setLoadingModels(false);
      }
    };

    fetchModelsByCategory();

    return () => {
      ignore = true;
    };
  }, [http, selectedProductId, setValue]);

  useEffect(() => {
    let ignore = false;

    const fetchColorsAndCapacitiesByModel = async () => {
      const prevModelId = prevSelectedModelIdRef.current;
      prevSelectedModelIdRef.current = selectedModelId;

      if (!selectedModelId) {
        setFilteredColorOptions([]);
        setFilteredCapacityOptions([]);
        if (!isHydratingFromApiRef.current) {
          setValue("color", "");
          setValue("capacity", "");
        }
        return;
      }

      const shouldResetChildren =
        !isHydratingFromApiRef.current &&
        prevModelId !== undefined &&
        String(prevModelId) !== String(selectedModelId);

      try {
        setLoadingColors(true);
        setLoadingCapacities(true);

        if (shouldResetChildren) {
          setValue("color", "");
          setValue("capacity", "");
        }

        const [cRes, capRes] = await Promise.all([
          http.get("/colors", {
            params: {
              filters: { model: { id: { $eq: selectedModelId } } },
              sort: ["sequence:asc", "id:asc"],
              pagination: { pageSize: 200 },
            },
          }),
          http.get("/capacities", {
            params: {
              filters: { model: { id: { $eq: selectedModelId } } },
              sort: ["sequence:asc", "id:asc"],
              pagination: { pageSize: 200 },
            },
          }),
        ]);

        if (ignore) return;

        setFilteredColorOptions(
          (cRes?.data?.data || []).map((it) => ({
            value: String(it.id),
            label: it.name || "-",
          }))
        );

        setFilteredCapacityOptions(
          (capRes?.data?.data || []).map((it) => ({
            value: String(it.id),
            label: it.size || "-",
          }))
        );
      } catch (e) {
        console.error("load color/capacity by model error:", e);
        if (!ignore) {
          setFilteredColorOptions([]);
          setFilteredCapacityOptions([]);
        }
      } finally {
        if (!ignore) {
          setLoadingColors(false);
          setLoadingCapacities(false);
        }
      }
    };

    fetchColorsAndCapacitiesByModel();

    return () => {
      ignore = true;
    };
  }, [http, selectedModelId, setValue]);

  const [entity, setEntity] = useState(null);
  const isRedeemed = isEdit && String(entity?.pawn_status || "").toLowerCase() === "redeemed";
  const pawnReportUrl = useMemo(
    () => normalizeUploadUrl(entity?.report_pdf_path || ""),
    [entity?.report_pdf_path]
  );
  const handleOpenPawnBill = React.useCallback(async () => {
    if (!id) return;
    setApiErr("");
    try {
      const latestUrl = pawnReportUrl || (await fetchPawnReportPdfUrlById(http, id));
      if (!latestUrl) {
        setApiErr("ไม่พบไฟล์บิลของรายการนี้");
        return;
      }
      setReportPreviewUrl(latestUrl);
      setReportPreviewOpen(true);
    } catch (e) {
      console.error("open pawn bill error:", e);
      setApiErr("เปิดบิลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
    }
  }, [id, http, pawnReportUrl]);

  const displayedProductOptions = useMemo(
    () => ensureSelectedOption(productOptions, relationToIdString(entity?.category), entity?.category?.name || entity?.product_name),
    [productOptions, entity]
  );
  const displayedModelOptions = useMemo(
    () => ensureSelectedOption(filteredModelOptions, relationToIdString(entity?.model), entity?.model?.name),
    [filteredModelOptions, entity]
  );
  const displayedColorOptions = useMemo(
    () => ensureSelectedOption(filteredColorOptions, relationToIdString(entity?.color), entity?.color?.name),
    [filteredColorOptions, entity]
  );
  const displayedCapacityOptions = useMemo(
    () => ensureSelectedOption(filteredCapacityOptions, relationToIdString(entity?.capacity), entity?.capacity?.size || entity?.capacity?.name),
    [filteredCapacityOptions, entity]
  );
  const displayedEquipmentOptions = useMemo(() => {
    const selectedValue = relationToIdString(entity?.equipment);
    const matched = (equipmentOptions || []).find((it) => String(it?.value) === String(selectedValue));
    return ensureSelectedOption(
      equipmentOptions,
      selectedValue,
      matched?.label || entity?.equipment
    );
  }, [equipmentOptions, entity]);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();

    if (!isEdit) {
      const now = new Date();
      setHeader({
        title: "เพิ่มรายการจำนำ",
        createdAtText: formatThaiDateTimeFromISO(new Date().toISOString()),
      });

      reset((prev) => ({
        ...prev,
        buyInDateTime: now,
        dueDate: getAutoDueDateFromBuyIn(now),
        warranty_end_date: null,
        battery: "",
        cycle: "",
        warranty: "",
        imei: "",
        customerName: "",
        customerPhone: "",
        social1: "",
        social2: "",
        description: "",
        note: "",
        receivePrice: "",
        product_name: "",
        model: "",
        color: "",
        capacity: "",
        accessories: "",
      }));

      return () => ac.abort();
    }

    (async () => {
      try {
        setApiErr("");
        const res = await http.get(`/pawns/${id}`, { signal: ac.signal });
        const fetchedEntity = res?.data?.data;
        setEntity(fetchedEntity); // เก็บ entity ไว้เพื่อเช็ค pawn_status

        if (!alive || !fetchedEntity) return;

        setHeader({
          title: `เลขฝาก ${fetchedEntity?.id ?? id}`,
          createdAtText: formatThaiDateTimeFromISO(fetchedEntity?.createdAt),
        });

        reset({
          buyInDateTime: toDateValue(fetchedEntity.buy_in_date),
          dueDate: toDateValue(fetchedEntity.due_date),
          warranty_end_date: fetchedEntity.warranty_end_date ? toDateValue(fetchedEntity.warranty_end_date) : null,
          battery: String(fetchedEntity.battery_health ?? ""),
          cycle: String(fetchedEntity.cycle_count ?? ""),
          warranty: normalizeWarrantyType(fetchedEntity.warranty_status),
          imei: String(fetchedEntity.imei ?? "").toUpperCase(),
          customerName: fetchedEntity.customer_name ?? "",
          customerPhone: fetchedEntity.customer_phone ?? "",
          social1: relationToIdString(fetchedEntity.social_media_1),
          social2: relationToIdString(fetchedEntity.social_media_2),
          description: fetchedEntity.notes ?? "",
          note: "",
          receivePrice: String(fetchedEntity.amount ?? ""),
          product_name: relationToIdString(fetchedEntity.category),
          model: relationToIdString(fetchedEntity.model),
          color: relationToIdString(fetchedEntity.color),
          capacity: relationToIdString(fetchedEntity.capacity),
          accessories: relationToIdString(fetchedEntity.equipment),
        });
        setTimeout(() => {
          isHydratingFromApiRef.current = false;
        }, 0);
      } catch (e) {
        if (e?.name === "CanceledError" || e?.name === "AbortError") return;
        console.error("GET /pawns/:id error:", e);
        setApiErr("โหลดข้อมูลจำนำไม่สำเร็จ");
        isHydratingFromApiRef.current = false;
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [http, id, isEdit, reset]);

  const onSubmit = async (data) => {
    if (isRedeemed) {
      setApiErr("รายการนี้ไถ่ถอนแล้ว ไม่สามารถแก้ไขข้อมูลได้");
      return;
    }

    setSubmitting(true);
    setApiErr("");

    try {
      const buyInDate = toDateValue(data.buyInDateTime) || new Date();
      const dueDate = isEdit
        ? toDateValue(data.dueDate)
        : getAutoDueDateFromBuyIn(buyInDate);
      const warrantyExpire = toDateValue(data.warranty_end_date);
      const selectedProduct = (productOptions || []).find(
        (it) => String(it?.value) === String(data.product_name || "")
      );
      const selectedModel = (displayedModelOptions || []).find(
        (it) => String(it?.value) === String(data.model || "")
      );
      const selectedEquipment = (equipmentOptions || []).find(
        (it) => String(it?.value) === String(data.accessories || "")
      );
      const productName = String(selectedProduct?.label || "").trim();
      const modelName = String(selectedModel?.label || "").trim();
      const combinedProductName =
        productName && modelName ? `${productName} ${modelName}` : productName || modelName || "";

      const payload = {
        data: {
          product_name: combinedProductName,
          category: data.product_name ? Number(data.product_name) : null,
          model: String(data.model || ""),
          color: String(data.color || ""),
          capacity: String(data.capacity || ""),
          equipment: String(selectedEquipment?.label || data.accessories || ""),

          battery_health: String(data.battery || ""),
          cycle_count: String(data.cycle || ""),
          imei: String(data.imei || "").trim().toUpperCase(),
          warranty_status: String(data.warranty || ""),
          warranty_end_date: warrantyExpire ? warrantyExpire.toISOString().slice(0, 10) : null,

          amount: Number(String(data.receivePrice || 0).replace(/,/g, "")) || 0,
          interest_rate: 0,

          customer_name: String(data.customerName || ""),
          customer_phone: String(data.customerPhone || ""),
          social_media_1: String(data.social1 || ""),
          social_media_2: String(data.social2 || ""),

          due_date: dueDate ? dueDate.toISOString().slice(0, 10) : null,
          buy_in_date: buyInDate.toISOString(),

          pawn_status: "active",

          notes: [data.description, data.note].filter(Boolean).join("\n"),

          images: [],
          user_fullname: "string",
        },
      };

      if (isEdit) {
        const updatedRes = await http.put(`/pawns/${id}`, payload);
        const updatedReportUrlFromResponse = extractPawnReportPdfUrlFromResponse(updatedRes?.data);
        await delay(3000);
        const updatedRefreshedReportUrl = await fetchPawnReportPdfUrlById(http, id);
        const updatedReportUrl = updatedRefreshedReportUrl || updatedReportUrlFromResponse;

        if (updatedReportUrl) {
          setReportPreviewUrl(updatedReportUrl);
          setReportPreviewOpen(true);
        } else {
          navigate(-1);
        }
      } else {
        const createdRes = await http.post("/pawns", payload);
        const createdPawnIdOrDocumentId =
          createdRes?.data?.data?.id ||
          createdRes?.data?.data?.documentId ||
          createdRes?.data?.id ||
          createdRes?.data?.documentId ||
          createdRes?.data?.data?.id;

        let createdReportUrl = extractPawnReportPdfUrlFromResponse(createdRes?.data);
        if (!createdReportUrl && createdPawnIdOrDocumentId) {
          createdReportUrl = await fetchPawnReportPdfUrlWithRetry(http, createdPawnIdOrDocumentId);
        }

        if (createdReportUrl) {
          setReportPreviewUrl(createdReportUrl);
          setReportPreviewOpen(true);
        } else {
          navigate("/app/pawn");
        }
      }
    } catch (e) {
      console.error(`${isEdit ? "PUT" : "POST"} /pawns error:`, e);
      setApiErr(isEdit ? "แก้ไขรายการจำนำไม่สำเร็จ" : "บันทึกรายการจำนำไม่สำเร็จ");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (isEdit) return;
    setValue("dueDate", getAutoDueDateFromBuyIn(selectedBuyInDateTime), {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [isEdit, selectedBuyInDateTime, setValue]);

  return (
    <div className="p-4 bg-[#F5F5F5] min-h-full">
      <PageHeader
        icon={Banknote}
        breadcrumb={
          <span className="flex items-center gap-2">
            <span className="hover:underline cursor-pointer" onClick={() => navigate(-1)}>
              จำนำ
            </span>
            <span className="text-slate-300">/</span>
            <span className="font-semibold text-[#047DED]">เพิ่มรายการจำนำ</span>
          </span>
        }
        title={header.title}
        subtitle={
          <span className="text-sm flex items-center gap-2 text-[#1D1616B2]">
            <CalendarDays size={16} />
            ซื้อเข้าเมื่อ {header.createdAtText}
          </span>
        }
        showExport={false}
        extraActions={
          isEdit && entity?.pawn_status === "active" ? (
            <button
              type="button"
              onClick={() => setOpenRedeem(true)}
              className="h-10 rounded-xl bg-[#047DED] px-5 text-sm font-extrabold text-white hover:bg-[#3EA2FE] active:scale-[0.99]"
              disabled={redeeming}
            >
              ไถ่ถอนการจำนำ
            </button>
          ) : isRedeemed ? (
            <button
              type="button"
              onClick={handleOpenPawnBill}
              className="inline-flex items-center gap-2 rounded-xl bg-[#047DED0D] px-4 py-2 text-sm font-semibold text-[#047DED] hover:text-[#FFF] hover:bg-[#047DED]"
            >
              <FileText size={16} />
              บิล
            </button>
          ) : null
        }
      />

      <RedeemModal
        open={openRedeem}
        onClose={() => setOpenRedeem(false)}
        defaultPhone=""
        onSubmit={(draft) => {
          setRedeemDraft({
            ...draft,
          });
          setOpenRedeem(false);
        }}
      />

      <ConfirmDeleteModal
        open={openDelete}
        onCancel={() => setOpenDelete(false)}
        onConfirm={handleDelete}
        loading={deleting}
        title="ยืนยันการลบรายการ"
        desc="เมื่อลบแล้วจะไม่สามารถกู้คืนได้ ต้องการลบรายการนี้ใช่หรือไม่?"
      />
      <ReportPreviewModal
        open={reportPreviewOpen}
        url={reportPreviewUrl}
        onClose={() => {
          setReportPreviewOpen(false);
          navigate(-1);
        }}
      />

      <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)} className="space-y-4 mt-4">
        <div className="rounded-2xl bg-white shadow-sm">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="text-lg font-semibold text-slate-800">บันทึกรายการ</div>
            <div className="flex items-center gap-3">
              {isEdit ? (
                <button
                  type="button"
                  onClick={handleOpenPawnBill}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#047DED0D] px-4 py-2 text-sm font-semibold text-[#047DED] hover:text-[#FFF] hover:bg-[#047DED]"
                  disabled={submitting}
                >
                  <FileText size={16} />
                  ใบสัญญาจำนำ
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="h-10 rounded-xl border border-slate-200 bg-white px-5 text-sm font-bold hover:bg-slate-50"
                disabled={submitting}
              >
                ยกเลิก
              </button>
              {!isRedeemed ? (
                <button
                  type="submit"
                  disabled={submitting}
                  className={classNames(
                    "h-10 rounded-xl bg-[#111] px-6 text-sm font-bold text-white shadow-md hover:opacity-95 active:scale-[0.99]",
                    submitting && "opacity-60 cursor-not-allowed"
                  )}
                >
                  {submitting ? "กำลังบันทึก..." : "บันทึก"}
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {apiErr && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {apiErr}
          </div>
        )}

        <fieldset disabled={isRedeemed} className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-extrabold text-slate-800">
              <Smartphone size={16} />
              รายละเอียดสินค้าที่ต้องการจำนำ
            </div>

            <div className="mt-3">
              <UnderlineDatePicker
                label="สินค้านำเข้ามือวันที่"
                name="buyInDateTime"
                control={control}
                errors={errors}
                required={true}
                placeholder=""
                showTimeSelect={true}
              />

              <UnderlineSelect
                label="ชื่อสินค้า"
                name="product_name"
                register={register}
                required
                errors={errors}
                options={displayedProductOptions}
                placeholder="เลือกข้อมูล"
              />

              <UnderlineSelect
                label="Model"
                name="model"
                register={register}
                required
                errors={errors}
                options={displayedModelOptions}
                disabled={!selectedProductId || loadingModels}
                placeholder={
                  !selectedProductId
                    ? "กรุณาเลือกชื่อสินค้าก่อน"
                    : loadingModels
                      ? "กำลังโหลด..."
                      : "เลือกข้อมูล"
                }
              />

              <UnderlineSelect
                label="สี"
                name="color"
                register={register}
                required
                errors={errors}
                options={displayedColorOptions}
                disabled={!selectedModelId || loadingColors}
                placeholder={
                  !selectedProductId
                    ? "กรุณาเลือกชื่อสินค้าก่อน"
                    : !selectedModelId
                      ? "กรุณาเลือก Model ก่อน"
                      : loadingColors
                        ? "กำลังโหลด..."
                        : "เลือกข้อมูล"
                }
              />

              <UnderlineSelect
                label="ความจุ"
                name="capacity"
                register={register}
                required
                errors={errors}
                options={displayedCapacityOptions}
                disabled={!selectedModelId || loadingCapacities}
                placeholder={
                  !selectedProductId
                    ? "กรุณาเลือกชื่อสินค้าก่อน"
                    : !selectedModelId
                      ? "กรุณาเลือก Model ก่อน"
                      : loadingCapacities
                        ? "กำลังโหลด..."
                        : "เลือกข้อมูล"
                }
              />

              <UnderlineSelect
                label="อุปกรณ์"
                name="accessories"
                register={register}
                required={true}
                errors={errors}
                options={displayedEquipmentOptions}
                disabled={loadingEquipments}
                placeholder={loadingEquipments ? "กำลังโหลด..." : "เลือกข้อมูล"}
              />

              <UnderlineInput
                label="ความจุแบตเตอรี่สูงสุด"
                name="battery"
                register={register}
                errors={errors}
                required={false}
                placeholder="กรอกข้อมูล"
                maxValue={100}
              />

              <UnderlineInput
                label="รอบชาร์จ"
                name="cycle"
                register={register}
                errors={errors}
                required={false}
                placeholder="กรอกข้อมูล"
                inputMode="numeric"
                onlyDigits
              />

              <UnderlineSelect
                label="ประกัน"
                name="warranty"
                register={register}
                required={false}
                errors={errors}
                options={warrantyOptions}
                placeholder="เลือกข้อมูล"
              />

              {isWarrantyRemaining && (
                <UnderlineDatePicker
                  label="วันหมดอายุประกัน"
                  name="warranty_end_date"
                  control={control}
                  errors={errors}
                  required={false}
                  placeholder="วัน/เดือน/ปีพ.ศ."
                  thaiBuddhistInput
                />
              )}

              <UnderlineInput
                label="IMEI"
                name="imei"
                register={register}
                errors={errors}
                required={false}
                placeholder="กรอก IMEI"
                maxLength={15}
                forceUppercase
                forceAlphanumeric
              />

              <UnderlineTextarea
                label="คำอธิบาย"
                name="description"
                register={register}
                required={false}
                errors={errors}
                rows={3}
                placeholder="เพิ่มเติม"
              />

              <UnderlineTextarea
                label="หมายเหตุ"
                name="note"
                register={register}
                required={false}
                errors={errors}
                rows={3}
                placeholder="เพิ่มเติม"
              />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-extrabold text-slate-800">
              <Phone size={16} />
              ช่องทางการติดต่อ
            </div>

            <div className="mt-2">
              <UnderlineInput
                label="ชื่อลูกค้า:"
                name="customerName"
                register={register}
                errors={errors}
                required={true}
                placeholder="กรอกข้อมูล"
              />

              <UnderlineInput
                label="เบอร์โทรศัพท์ของผู้ซื้อ:"
                name="customerPhone"
                maxNumberLength={10}
                type="number"
                register={register}
                errors={errors}
                required={true}
                placeholder="กรอกข้อมูล"
              />

              <UnderlineSelect
                label="Social Media ที่ 1"
                name="social1"
                register={register}
                errors={errors}
                required={false}
                options={contactInfoOptions}
                disabled={loadingContactInfos}
                placeholder={loadingContactInfos ? "กำลังโหลด..." : "เลือกข้อมูล"}
              />

              <UnderlineSelect
                label="Social Media ที่ 2"
                name="social2"
                register={register}
                errors={errors}
                required={false}
                options={contactInfoOptions}
                disabled={loadingContactInfos}
                placeholder={loadingContactInfos ? "กำลังโหลด..." : "เลือกข้อมูล"}
              />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-extrabold text-slate-800">
              <Banknote size={16} />
              ราคารับ
            </div>

            <UnderlineInput
              label="ราคารับ"
              name="receivePrice"
              register={register}
              errors={errors}
              required={true}
              placeholder="฿0"
            />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-extrabold text-slate-800">
              เลยกำหนดวันที่
            </div>

            <UnderlineDatePicker
              label="เลยกำหนดวันที่จำนำ"
              name="dueDate"
              control={control}
              errors={errors}
              required={false}
              placeholder=""
              disabled={!isEdit}
            />
          </div>
        </fieldset>

        {isEdit && !isRedeemed && (
          <div className="p-6 space-y-2 text-[#FF1607] font-semibold text-center">
            <button
              type="button"
              onClick={() => setOpenDelete(true)}
              className="hover:underline"
              disabled={submitting || deleting}
            >
              ลบรายการ
            </button>
          </div>
        )}

        <div className="h-4" />
      </form>
    </div>
  );
}